import * as React from 'react';
import Values from 'values.js';
import { useTheme } from 'src/providers/ThemeProvider';
import { OptionInputStyles, OptionInputTypes } from 'src/types/IOptions';
import { StyledPlanOption } from './StyledPlanOption';

interface IOptionColors {
  background: string;
  backgroundSelected: string;
  border: string;
  borderSelected: string;
  inputBackground: string;
  inputBorder: string;
  inputHoverCheckboxBackground: string;
  text: string;
  textSelected: string;
}

interface IPlanOptionProps {
  children: React.ReactNode;
  contractId: string;
  inputStyle: OptionInputStyles;
  inputType: OptionInputTypes;
  isSelected: boolean;
  onChange: (contractId: string) => void;
  className?: string;
  renderAsButton?: boolean;
}

export function PlanOption(props: IPlanOptionProps) {
  const { merchantColor } = useTheme();
  const {
    children,
    contractId,
    inputStyle,
    inputType,
    isSelected,
    onChange,
    renderAsButton = false,
    ...rest
  } = props;

  const isCheckboxOrRadio = React.useMemo(() => (
    inputStyle === OptionInputStyles.RADIO || inputStyle === OptionInputStyles.CHECKBOX
  ), [inputStyle]);

  const color = React.useMemo(() => new Values(merchantColor), [merchantColor]);
  const colors: IOptionColors = React.useMemo(() => ({
    background: color.tint(100).hexString(), // Completely lighten (to white)
    backgroundSelected: color.tint(90).hexString(),
    border: 'var(--light-gray)',
    borderSelected: color.hexString(),
    inputBackground: color.hexString(),
    inputBorder: color.hexString(),
    inputHoverCheckboxBackground: color.tint(95).hexString(),
    // NOTE: this logic doesn't work for light brand colors (closer to white than to black)
    text: '#000000',
    textSelected: color.hexString(),
  }), [color]);

  const onInputChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(contractId);

    // Special blur handling to remove the focus state
    if (inputType === 'checkbox') {
      e.currentTarget.blur();
    }
  }, [onChange, inputType]);

  return (
    <StyledPlanOption
      colors={colors}
      isCheckboxOrRadio={isCheckboxOrRadio}
      isSelected={isSelected}
      {...(renderAsButton ? { as: 'button' } : {})}
      {...rest}
    >
      <label className={`${inputStyle}-label`}>
        <input
          checked={isSelected}
          className={inputStyle}
          data-id={contractId}
          id={contractId}
          name="contracts"
          onChange={onInputChanged}
          type={inputType}
          value={contractId}
          aria-label={`contract-${contractId}`}
        />
        <span className="info">
          {children}
        </span>
      </label>
    </StyledPlanOption>
  );
}
